const defaultOptions = {
  enabled: true,
  leading: false,
  onError: () => {
  },
  onSettled: () => {
  },
  onSuccess: () => {
  },
  trailing: true,
  wait: 0
};
class AsyncDebouncer {
  constructor(fn, initialOptions) {
    this.fn = fn;
    this._abortController = null;
    this._canLeadingExecute = true;
    this._errorCount = 0;
    this._isExecuting = false;
    this._isPending = false;
    this._settleCount = 0;
    this._successCount = 0;
    this._timeoutId = null;
    this._options = {
      ...defaultOptions,
      ...initialOptions
    };
  }
  /**
   * Updates the debouncer options
   * Returns the new options state
   */
  setOptions(newOptions) {
    this._options = { ...this._options, ...newOptions };
    if (!this._options.enabled) {
      this._isPending = false;
    }
  }
  /**
   * Returns the current debouncer options
   */
  getOptions() {
    return this._options;
  }
  /**
   * Attempts to execute the debounced function
   * If a call is already in progress, it will be queued
   */
  async maybeExecute(...args) {
    this._cancel();
    this._lastArgs = args;
    if (this._options.leading && this._canLeadingExecute) {
      this._canLeadingExecute = false;
      await this.executeFunction(...args);
      return this._lastResult;
    }
    if (this._options.trailing) {
      this._isPending = true;
    }
    return new Promise((resolve) => {
      this._timeoutId = setTimeout(async () => {
        if (this._options.trailing && this._lastArgs) {
          await this.executeFunction(...this._lastArgs);
        }
        this._canLeadingExecute = true;
        resolve(this._lastResult);
      }, this._options.wait);
    });
  }
  async executeFunction(...args) {
    if (!this._options.enabled) return void 0;
    this._abortController = new AbortController();
    try {
      this._isExecuting = true;
      this._lastResult = await this.fn(...args);
      this._successCount++;
      this._options.onSuccess(this._lastResult, this);
    } catch (error) {
      this._errorCount++;
      this._options.onError(error, this);
    } finally {
      this._isExecuting = false;
      this._isPending = false;
      this._settleCount++;
      this._abortController = null;
      this._options.onSettled(this);
    }
    return this._lastResult;
  }
  /**
   * Cancel without resetting _canLeadingExecute
   */
  _cancel() {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._lastArgs = void 0;
    this._isPending = false;
    this._isExecuting = false;
  }
  /**
   * Cancels any pending execution or aborts any execution in progress
   */
  cancel() {
    this._canLeadingExecute = true;
    this._cancel();
  }
  /**
   * Returns the last result of the debounced function
   */
  getLastResult() {
    return this._lastResult;
  }
  /**
   * Returns the number of times the function has been executed successfully
   */
  getSuccessCount() {
    return this._successCount;
  }
  /**
   * Returns the number of times the function has settled (completed or errored)
   */
  getSettleCount() {
    return this._settleCount;
  }
  /**
   * Returns the number of times the function has errored
   */
  getErrorCount() {
    return this._errorCount;
  }
  /**
   * Returns `true` if there is a pending execution queued up for trailing execution
   */
  getIsPending() {
    return this._options.enabled && this._isPending;
  }
  /**
   * Returns `true` if there is currently an execution in progress
   */
  getIsExecuting() {
    return this._isExecuting;
  }
}
function asyncDebounce(fn, initialOptions) {
  const asyncDebouncer = new AsyncDebouncer(fn, initialOptions);
  return asyncDebouncer.maybeExecute.bind(asyncDebouncer);
}
export {
  AsyncDebouncer,
  asyncDebounce
};
//# sourceMappingURL=async-debouncer.js.map
